import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useState,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";

import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useActionComment from "../../../../../hooks/useActionComment";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { CartonProductView } from "../../../../../models/carton";
import { Comment } from "../../../../../models/comment";
import { DecimalFormatType, formatDecimalValue } from "../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import Input from "../../../../Form/Input";
import Select from "../../../../Form/Select";
import InputMeasurement from "../../../../Form/InputMeasurement";
import CommentHistory from "../../../Comment/History";
import { CommentsContainer, Modal, ModalGrid } from "./styles";
import { AutoSuggest } from "../../../../Form";
import { CommodityCode } from "../../../../../models/commodity.code";
import {
  CodeType,
  CodeTypeNumber,
  formatHSCode,
} from "../../../../../pages/private/Shared/Settings/Configurations/HSCommodityCode/utils";
import CommodityCodeService from "../../../../../services/commodity.code.service";
import { useShipmentResources } from "../../../../Booking/context/Resources";
import { UNIT_OF_MEASURE_OPTIONS } from "Util/constants";

export interface EditProductModalHandles {
  open: (
    sscc: string,
    productIndex: number,
    product: CartonProductView,
    viewOnly?: boolean
  ) => void;
  close: () => void;
}

interface EditProductModalProps {
  closeModal?: () => void;
  onSubmit?: (sscc: string, productIndex: number, product: CartonProductView) => void;
  viewOnly?: boolean;
  showLength?: boolean;
  resourceCompanyId?: number;
  shouldShowUnitOfMeasure?: boolean;
  showOriginCriterion?: boolean;
}

const ModalEditProduct: ForwardRefRenderFunction<EditProductModalHandles, EditProductModalProps> = (
  props,
  ref
) => {
  const {
    onSubmit,
    viewOnly: viewOnlyProp = false,
    showLength = false,
    resourceCompanyId,
    shouldShowUnitOfMeasure = false,
    showOriginCriterion = false,
  } = props;

  const { t: translate } = useTranslation();

  const [viewOnly, setViewOnly] = useState<boolean>(viewOnlyProp);
  const [product, setProduct] = useState<CartonProductView>();
  const [sscc, setSscc] = useState<string>();
  const [productIndex, setProductIndex] = useState<number>();
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [comments, setComments] = useState<Comment[]>([]);
  const [countComments, setCountComments] = useState<number>(0);
  const [commentsPage, setCommentsPage] = useState<number>(0);
  const commentsPageSize = 5;

  const { getAllComments } = useActionComment();
  const { data: dataComments, isFetching: isFetchingComments } = getAllComments(
    [["createdAt", "DESC"]],
    product?.purchaseOrderProductId,
    "purchase_order_product",
    commentsPage,
    commentsPageSize,
    product?.purchaseOrderId
  );

  useEffect(() => {
    if (dataComments) {
      setComments((state) => [...state, ...dataComments.rows]);
      setCountComments(dataComments.count);
    }
  }, [dataComments]);

  const hasMoreComments = useMemo(
    () => countComments > (commentsPage + 1) * commentsPageSize,
    [countComments, commentsPage]
  );

  const [openModal, setOpenModal] = useState<boolean>(false);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const open = useCallback(
    (sscc: string, productIndex: number, product: CartonProductView, viewOnly: boolean = false) => {
      setOpenModal(true);
      setSscc(sscc);
      setProductIndex(productIndex);
      setProduct(product);
      setViewOnly(viewOnly);
    },
    []
  );

  const close = useCallback(() => {
    setOpenModal(false);
    setSscc(undefined);
    setProductIndex(undefined);
    setProduct(undefined);
    setErrors([]);
    setComments([]);
    setCountComments(0);
    setCommentsPage(0);
  }, []);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const schema = yup.object().shape({
    cubicVolume: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero")),
    weight: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero")),
    quantityInCarton: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero")),
    hsCode: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    unitOfMeasure: yup.string().when("shouldShowUnitOfMeasure", {
      is: true,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
    }),
    originCriterion: yup.string().when("$showOriginCriterion", {
      is: true,
      then: (schema) =>
        schema
          .required(translate("originCriterionMustBeWOrP"))
          .oneOf(["W", "P"], translate("originCriterionMustBeWOrP")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          cubicVolume: product?.cfsCbm,
          weight: product?.cfsWeight,
          quantityInCarton: product?.quantityInCarton,
          hsCode: product?.hsCode,
          unitOfMeasure: product?.unitOfMeasure,
          shouldShowUnitOfMeasure: shouldShowUnitOfMeasure,
          originCriterion: product?.originCriterion,
        },
        { abortEarly: false, context: { showOriginCriterion } }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  const handleOnSave = () => {
    if (!sscc || !product) return;
    if (!handleValidate()) return;

    onSubmit?.(sscc, productIndex ?? 0, product);
    close();
  };

  const renderFormattedValue = (value?: number) => {
    if (!value) return "";

    if (viewOnly) {
      return formatDecimalValue(DecimalFormatType.MAERSK, value, "");
    }

    return String(value);
  };

  return (
    <Modal
      title={`${viewOnly ? translate("viewProduct") : translate("editProduct")} - ${translate(
        "sku"
      )} ${product?.sku}`}
      open={openModal}
      handleClose={close}
      showActionButtons
      showModalFooter={!viewOnly}
      actionButtonProps={{
        onSave: handleOnSave,
        onCancel: () => close(),
        saveDisabled: viewOnly,
      }}
    >
      <ModalGrid
        container
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        spacing={2}
      >
        <Grid item xs={3}>
          <Input
            disabled
            name="product-description"
            label={translate("productName")}
            value={product?.description}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            disabled
            name="product-style"
            label={translate("styleCode")}
            value={product?.style}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="number"
            min={0}
            name="quantity"
            label={translate("quantityFullfilled")}
            value={String(product?.quantityInCarton)}
            onChange={(event) =>
              setProduct({
                ...product,
                quantityInCarton: Number(event.target.value),
              })
            }
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            disabled={viewOnly}
            error={fieldHasError("quantityInCarton")}
            errorMessage={getErrorByField("quantityInCarton")}
          />
        </Grid>
        <Grid item xs={3}>
          <InputMeasurement
            min={0}
            name="weight"
            label={`${translate("weight")} `}
            value={renderFormattedValue(Number(product?.cfsWeight || 0))}
            change={(value: number) =>
              setProduct({
                ...product,
                weight: value,
                cfsWeight: value,
              })
            }
            disabled={viewOnly}
            error={fieldHasError("weight")}
            errorMessage={getErrorByField("weight")}
            measurement={measurementWeight}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
        </Grid>
        <Grid item xs={3}>
          <InputMeasurement
            min={0}
            name="cbm"
            label={`${translate("volume")}`}
            value={renderFormattedValue(Number(product?.cfsCbm || 0))}
            change={(value: number) =>
              setProduct({
                ...product,
                cubicVolume: value,
                cfsCbm: value,
              })
            }
            disabled={viewOnly}
            error={fieldHasError("cubicVolume")}
            errorMessage={getErrorByField("cubicVolume")}
            measurement={measurement}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
        </Grid>
        <Grid item xs={3}>
          <AutoSuggest
            disabled={viewOnly}
            label={translate("hsCommodityCode")}
            placeholder={translate("typeInToSearch")}
            getOptionLabel={(option: CommodityCode) => option?.hscode}
            getOptionSelected={(option: CommodityCode, value: CommodityCode) =>
              option.id === value.id
            }
            renderOption={(props, option) => (
              <div {...props}>
                `${formatHSCode(option?.hscode)} - ${option?.description}`
              </div>
            )}
            inputValue={product?.hsCode && formatHSCode(product.hsCode)}
            onChange={(value: CommodityCode) => {
              if (!value?.hscode) return;
              setProduct({
                ...product,
                hsCode: value.hscode,
                commodityCodeId: value.id,
                commodityCode: value,
              } as CartonProductView);
            }}
            onSearch={(value) =>
              CommodityCodeService.getAll({
                companyId: resourceCompanyId,
                level: [
                  CodeTypeNumber[CodeType.HS_CODE],
                  CodeTypeNumber[CodeType.COMMODITY_CODE],
                ] as unknown as number,
                sortBy: JSON.stringify([["section", "ASC"]]),
                searchByOr: value
                  ? JSON.stringify([{ description: value }, { hscode: value }])
                  : "",
                include: JSON.stringify([
                  { association: "sectionEntity" },
                  { association: "chapterEntity" },
                ]),
              })
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="lining"
            label={translate("lining")}
            value={product?.lining}
            onChange={(event) =>
              setProduct({
                ...product,
                lining: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="fabric"
            label={translate("fabric")}
            value={product?.fabric}
            onChange={(event) =>
              setProduct({
                ...product,
                fabric: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="construction"
            label={translate("construction")}
            value={product?.construction}
            onChange={(event) =>
              setProduct({
                ...product,
                construction: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="composition"
            label={translate("composition")}
            value={product?.composition}
            onChange={(event) =>
              setProduct({
                ...product,
                composition: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="category"
            label={translate("category")}
            value={product?.category}
            onChange={(event) =>
              setProduct({
                ...product,
                category: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="manufacture-details"
            label={translate("manufacturerDetails")}
            value={product?.manufactureDetails}
            onChange={(event) =>
              setProduct({
                ...product,
                manufactureDetails: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        <Grid item xs={3}>
          <Input
            type="string"
            name="gender"
            label={translate("gender")}
            value={product?.gender}
            onChange={(event) =>
              setProduct({
                ...product,
                gender: event.target.value,
              })
            }
            disabled={viewOnly}
          />
        </Grid>
        {shouldShowUnitOfMeasure && (
          <Grid item xs={3}>
            <Select
              label={translate("unitOfMeasure")}
              value={product?.unitOfMeasure}
              onChange={(event) =>
                setProduct({
                  ...product,
                  unitOfMeasure: event.target.value as string,
                })
              }
              disabled={viewOnly}
              required={shouldShowUnitOfMeasure}
            >
              {UNIT_OF_MEASURE_OPTIONS(translate).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {showOriginCriterion ? (
          <Grid item xs={3}>
            <Select
              name="origin-criterion"
              label={translate("originCriterion")}
              value={product?.originCriterion || ""}
              onChange={(event) =>
                setProduct({
                  ...product,
                  originCriterion: event.target.value as string,
                })
              }
              disabled={viewOnly}
              error={fieldHasError("originCriterion")}
              errorMessage={getErrorByField("originCriterion")}
            >
              <MenuItem value="W">W</MenuItem>
              <MenuItem value="P">P</MenuItem>
            </Select>
          </Grid>
        ) : null}

        {showLength ? (
          <Grid item xs={3}>
            <Input
              type="string"
              name="length"
              label={translate("length")}
              value={product?.length}
              onChange={(event) =>
                setProduct({
                  ...product,
                  length: event.target.value,
                })
              }
              disabled={viewOnly}
            />
          </Grid>
        ) : null}
      </ModalGrid>
      {countComments > 0 && comments.length > 0 && (
        <CommentsContainer>
          <CommentHistory
            comments={comments}
            count={countComments}
            hasMore={hasMoreComments}
            isLoading={isFetchingComments}
            onLoadMore={() => setCommentsPage((state) => state + 1)}
          />
        </CommentsContainer>
      )}
    </Modal>
  );
};

export default forwardRef(ModalEditProduct);
